import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Check, X, GripVertical } from 'lucide-react';
import { planesPagoAPI } from '../services/api';
import { Modal, Button, Input, Card, Badge, Tooltip, useConfirm } from './ui';

/**
 * Panel administrativo de Planes de Pago.
 *
 * Cada plan agrupa N cuotas cuyos porcentajes suman 100%. El numero de periodo
 * es 1-based y se asigna automatico segun el orden de la lista en el modal —
 * si el user elimina el #2, los siguientes se re-numeran (#3 pasa a #2, etc).
 * La suma del 100% se valida en cliente y en servidor.
 */

const emptyPlan = { nombre: '', descripcion: '', activo: true, orden: 0, periodos: [] };

const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

const PlanesPagoPanel = () => {
    const confirm = useConfirm();
    const [planes, setPlanes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formModal, setFormModal] = useState({ open: false, editing: null, data: emptyPlan });
    const [saving, setSaving] = useState(false);

    useEffect(() => { cargar(); }, []);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await planesPagoAPI.getAll();
            const normalizados = (res.data || []).map(p => ({
                ...p,
                activo: !!p.activo,
                periodos: (p.periodos || []).map(x => ({ ...x, porcentaje: parseFloat(x.porcentaje) || 0 }))
            }));
            setPlanes(normalizados);
        } catch (err) {
            toast.error('No pudimos cargar los planes de pago');
        } finally {
            setLoading(false);
        }
    };

    const abrirCrear = () => {
        setFormModal({
            open: true,
            editing: null,
            data: { ...emptyPlan, orden: planes.length, periodos: [] }
        });
    };
    const abrirEditar = (p) => {
        setFormModal({
            open: true,
            editing: p.id,
            data: {
                nombre: p.nombre,
                descripcion: p.descripcion || '',
                activo: !!p.activo,
                orden: p.orden || 0,
                periodos: (p.periodos || []).map(x => ({
                    _key: `id-${x.id}`,
                    nombre: x.nombre,
                    porcentaje: x.porcentaje
                }))
            }
        });
    };
    const cerrarForm = () => setFormModal({ open: false, editing: null, data: emptyPlan });

    // Suma de porcentajes en tiempo real para la validacion + UI.
    const sumaPorcentajes = useMemo(() => {
        return round2((formModal.data.periodos || []).reduce((acc, p) => acc + (parseFloat(p.porcentaje) || 0), 0));
    }, [formModal.data.periodos]);

    const restanteParaCien = round2(100 - sumaPorcentajes);
    const puedeAgregarPeriodo = sumaPorcentajes < 100;
    const sumaOk = sumaPorcentajes === 100;

    // Agrega una nueva fila de periodo. Sugerimos como % lo que falta para llegar
    // a 100 — asi el user con 1 click deja el plan completo.
    const agregarPeriodo = () => {
        if (!puedeAgregarPeriodo) {
            toast.error('Ya tienes 100% asignado. Ajusta un período existente primero.');
            return;
        }
        setFormModal(m => {
            const nuevoNumero = m.data.periodos.length + 1;
            const restante = round2(100 - m.data.periodos.reduce((a, p) => a + (parseFloat(p.porcentaje) || 0), 0));
            return {
                ...m,
                data: {
                    ...m.data,
                    periodos: [
                        ...m.data.periodos,
                        {
                            _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                            nombre: `Período ${nuevoNumero}`,
                            porcentaje: restante > 0 ? restante : 0
                        }
                    ]
                }
            };
        });
    };

    const actualizarPeriodo = (idx, campo, valor) => {
        setFormModal(m => ({
            ...m,
            data: {
                ...m.data,
                periodos: m.data.periodos.map((p, i) => i === idx ? { ...p, [campo]: valor } : p)
            }
        }));
    };

    const eliminarPeriodo = (idx) => {
        setFormModal(m => ({
            ...m,
            data: { ...m.data, periodos: m.data.periodos.filter((_, i) => i !== idx) }
        }));
    };

    const handleGuardar = async () => {
        const { editing, data } = formModal;

        if (!data.nombre.trim()) return toast.error('El nombre del plan es obligatorio');
        if (data.periodos.length === 0) return toast.error('Agrega al menos un período de pago');
        if (sumaPorcentajes !== 100) {
            return toast.error(`La suma de porcentajes debe ser 100% (actual: ${sumaPorcentajes}%)`);
        }
        for (let i = 0; i < data.periodos.length; i++) {
            if (!String(data.periodos[i].nombre || '').trim()) {
                return toast.error(`El período #${i + 1} necesita un nombre`);
            }
        }

        setSaving(true);
        try {
            const payload = {
                nombre: data.nombre.trim(),
                descripcion: data.descripcion.trim() || null,
                activo: data.activo,
                orden: parseInt(data.orden, 10) || 0,
                periodos: data.periodos.map(p => ({
                    nombre: String(p.nombre).trim(),
                    porcentaje: round2(p.porcentaje)
                }))
            };
            if (editing) {
                await planesPagoAPI.update(editing, payload);
                toast.success('Plan actualizado');
            } else {
                await planesPagoAPI.create(payload);
                toast.success(`Plan "${payload.nombre}" creado`);
            }
            cerrarForm();
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActivo = async (p) => {
        try {
            await planesPagoAPI.update(p.id, { activo: !p.activo });
            cargar();
        } catch (err) {
            toast.error(err.message || 'No pudimos cambiar el estado');
        }
    };

    const handleEliminar = async (p) => {
        const ok = await confirm({
            title: '¿Eliminar plan de pago?',
            message: <p>Se eliminará <strong>{p.nombre}</strong> y todos sus períodos. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await planesPagoAPI.delete(p.id);
            toast.success('Plan eliminado');
            cargar();
        } catch (err) {
            toast.error(err.message || 'No se pudo eliminar');
        }
    };

    if (loading) return <div className="loading">Cargando planes de pago...</div>;

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Planes de pago</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <div>
                        <h3 className="h3">Plantillas de planes</h3>
                        <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                            Define planes con cuotas por porcentaje (ej. 50% al inicio + 50% al entregar). Los porcentajes de cada plan deben sumar exactamente 100%.
                        </p>
                    </div>
                    <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={abrirCrear}>
                        Nuevo plan
                    </Button>
                </div>

                {planes.length === 0 ? (
                    <div className="empty-state" style={{ padding: 32, textAlign: 'center', color: 'rgb(var(--muted))' }}>
                        No hay planes de pago configurados. Crea el primero para empezar.
                    </div>
                ) : (
                <div className="prefijos-list">
                    {planes.map(p => (
                        <Card key={p.id} className={`prefijo-card ${!p.activo ? 'is-inactive' : ''}`}>
                            <Card.Body className="prefijo-card-body">
                                <div className="prefijo-main">
                                    <div className="prefijo-info">
                                        <div className="prefijo-nombre">
                                            {p.nombre}
                                            {!p.activo && <Badge color="neutral" size="sm">Inactivo</Badge>}
                                            <Badge color="info" size="sm">{p.periodos.length} cuota{p.periodos.length === 1 ? '' : 's'}</Badge>
                                        </div>
                                        {p.descripcion && <div className="prefijo-desc">{p.descripcion}</div>}
                                        {p.periodos.length > 0 && (
                                            <div className="plan-pago-periodos-preview">
                                                {p.periodos.map(per => (
                                                    <span key={per.id} className="plan-pago-chip">
                                                        <span className="plan-pago-chip-num">#{per.numero_periodo}</span>
                                                        <span className="plan-pago-chip-nombre">{per.nombre}</span>
                                                        <span className="plan-pago-chip-pct">{per.porcentaje}%</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="prefijo-actions">
                                    <Tooltip content={p.activo ? 'Desactivar' : 'Activar'}>
                                        <Button
                                            variant={p.activo ? 'ghost' : 'soft'}
                                            size="sm"
                                            leftIcon={p.activo ? <X size={13} /> : <Check size={13} />}
                                            onClick={() => handleToggleActivo(p)}
                                        >
                                            {p.activo ? 'Desactivar' : 'Activar'}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Editar">
                                        <Button variant="ghost" size="sm" leftIcon={<Pencil size={13} />} onClick={() => abrirEditar(p)}>
                                            Editar
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Eliminar">
                                        <Button variant="ghost" size="sm" leftIcon={<Trash2 size={13} />} onClick={() => handleEliminar(p)} />
                                    </Tooltip>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
                )}

                <Modal
                    open={formModal.open}
                    onClose={cerrarForm}
                    title={formModal.editing ? 'Editar plan de pago' : 'Nuevo plan de pago'}
                    size="lg"
                    footer={
                        <>
                            <Button variant="ghost" size="sm" onClick={cerrarForm}>Cancelar</Button>
                            <Button variant="primary" size="sm" onClick={handleGuardar} disabled={saving || !sumaOk}>
                                {saving ? 'Guardando...' : (formModal.editing ? 'Guardar cambios' : 'Crear plan')}
                            </Button>
                        </>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <label className="label">Nombre <span className="text-danger">*</span></label>
                            <Input
                                value={formModal.data.nombre}
                                onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, nombre: e.target.value } }))}
                                placeholder="Ej: 50/50, Adelanto 30% + saldo, 3 cuotas iguales"
                                maxLength={120}
                            />
                        </div>
                        <div>
                            <label className="label">Descripción <span className="text-xs text-muted">(opcional)</span></label>
                            <Input
                                value={formModal.data.descripcion}
                                onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, descripcion: e.target.value } }))}
                                placeholder="Cuándo se cobra cada cuota, condiciones, etc."
                                maxLength={500}
                            />
                        </div>

                        {/* Editor de periodos — cada fila se renumera automatico. La suma
                            en tiempo real avisa cuánto falta o si hay que ajustar. */}
                        <div className="plan-pago-periodos-editor">
                            <div className="plan-pago-periodos-editor-head">
                                <div>
                                    <label className="label" style={{ margin: 0 }}>Períodos de pago</label>
                                    <p className="text-xs text-muted" style={{ marginTop: 2 }}>
                                        La suma debe ser 100%. Se numeran automáticamente por orden.
                                    </p>
                                </div>
                                <Badge color={sumaOk ? 'success' : (sumaPorcentajes > 100 ? 'danger' : 'warning')} size="sm">
                                    {sumaPorcentajes}% / 100%
                                </Badge>
                            </div>

                            {formModal.data.periodos.length === 0 ? (
                                <div className="plan-pago-periodos-empty">
                                    Aún no hay períodos. Agrega el primero para empezar.
                                </div>
                            ) : (
                                <div className="plan-pago-periodos-list">
                                    {formModal.data.periodos.map((p, idx) => (
                                        <div key={p._key} className="plan-pago-periodo-row">
                                            <span className="plan-pago-periodo-num" aria-label={`Período número ${idx + 1}`}>
                                                <GripVertical size={12} aria-hidden="true" />
                                                #{idx + 1}
                                            </span>
                                            <Input
                                                value={p.nombre}
                                                onChange={e => actualizarPeriodo(idx, 'nombre', e.target.value)}
                                                placeholder="Nombre del período"
                                                maxLength={120}
                                            />
                                            <div className="plan-pago-periodo-pct">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    value={p.porcentaje}
                                                    onChange={e => actualizarPeriodo(idx, 'porcentaje', e.target.value)}
                                                />
                                                <span className="plan-pago-periodo-pct-symbol">%</span>
                                            </div>
                                            <Tooltip content="Eliminar período">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    leftIcon={<Trash2 size={13} />}
                                                    onClick={() => eliminarPeriodo(idx)}
                                                />
                                            </Tooltip>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="plan-pago-periodos-actions">
                                <Button
                                    variant="soft"
                                    size="sm"
                                    leftIcon={<Plus size={13} />}
                                    onClick={agregarPeriodo}
                                    disabled={!puedeAgregarPeriodo}
                                >
                                    Agregar período
                                </Button>
                                {!sumaOk && (
                                    <span className="plan-pago-periodos-hint">
                                        {sumaPorcentajes > 100
                                            ? `Ajusta: te pasas por ${round2(sumaPorcentajes - 100)}%`
                                            : `Faltan ${restanteParaCien}% para llegar a 100%`}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    </>
    );
};

export default PlanesPagoPanel;
